import { useState, useEffect, useRef } from "react";

import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import UpdateIcon from "@mui/icons-material/Update";
import Tooltip from "@mui/material/Tooltip";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import Box from "@mui/material/Box";

import VersionCardPreviewItem from "./VersionCardPreviewItem";
import VersionMenu from "./VersionMenu";

import { useDataSource, useAuthController } from "@camberi/firecms";
import versionsCollection from "../Collections/versionsCollection";
import LayersClearIcon from "@mui/icons-material/LayersClear";

import { UnrealEngineLogoIcon } from "../CustomIcons/CustomIcons";

import { useDebounce } from "usehooks-ts";

const DEBUG_PREVIEW_ORDER = false;

const styles = {
	version_overlay: {
		height: "32px",
		ml: -9,
		mt: 2,
		color: "black",
		pr: "5px",
		pl: "5px",
		background: "rgba(0,0,0,.5)",
		borderRadius: "5px",
		zIndex: 1,
	},

	time: {
		top: "20px",
		pr: "5px",
		pl: "5px",
		zIndex: 1,
	},

	container_rows: {
		display: "flex",
		position: "relative",
		flexDirection: "row",
		width: "100%",
	},

	container_row_item_right: {
		position: "relative",
		width: "50%",
		height: "50%",
		pl: "3px",
		pr: "8px",
		pt: "8px",
		pb: "8px",
	},

	preview_debug_badge: {
		position: "absolute",
		top: 6,
		left: 6,
		zIndex: 50,
		pointerEvents: "none",
		fontSize: "10px",
		lineHeight: "12px",
		fontWeight: 800,
		color: "black",
		background: "rgba(255, 214, 0, 0.94)",
		border: "1px solid rgba(0,0,0,0.45)",
		borderRadius: "4px",
		px: "4px",
		py: "2px",
		maxWidth: "calc(100% - 12px)",
		overflow: "hidden",
		textOverflow: "ellipsis",
		whiteSpace: "nowrap",
		boxShadow: "0 1px 4px rgba(0,0,0,0.35)",
	},
};

const isPreviewOrderDebugEnabled = () => {
	if (!DEBUG_PREVIEW_ORDER) {
		return false;
	}

	if (typeof window === "undefined") {
		return DEBUG_PREVIEW_ORDER;
	}

	return (window as any).__BOOST_PREVIEW_ORDER_DEBUG !== false;
};

const stringifyPreviewDebugState = (value: any) => {
	try {
		return JSON.stringify(value, null, 2);
	} catch (err: any) {
		return JSON.stringify(
			{
				error: "Failed to stringify preview debug state",
				message: err?.message || String(err),
			},
			null,
			2
		);
	}
};

const getShortDebugValue = (value: any, maxLength = 12) => {
	const text = String(value || "");

	if (text.length <= maxLength) {
		return text;
	}

	return `${text.slice(0, 5)}…${text.slice(-5)}`;
};

const getFileTypeLabel = (fileType: any) => {
	const filetypeString = String(fileType || "").split("/")?.[1] || "";

	if (!filetypeString) {
		return "";
	}

	if (filetypeString === "quicktime") {
		return "mov";
	}

	if (filetypeString === "jpeg") {
		return "jpg";
	}

	return filetypeString;
};

const isCoconutJobReady = (job: any) => {
	if (!job) {
		return false;
	}

	const status = String(job?.status || "");

	if (status === "ready.coconut.job.completed") {
		return true;
	}

	if (status === "ready.coconut.preview.ready") {
		return true;
	}

	if (status.startsWith("ready.")) {
		return true;
	}

	return Boolean(job?.preview?.img_cover || job?.preview?.img_hover_thumb_10 || job?.preview?.img_hover_thumb_20 || job?.preview?.img_hover_thumb_30 || job?.preview?.img_hover_thumb_40 || job?.preview?.img_hover_thumb_50);
};

const isImageJobReady = (job: any) => {
	if (!job) {
		return false;
	}

	const status = String(job?.status || "");

	if (status === "ready.image.preview.ready") {
		return true;
	}

	if (status.startsWith("ready.")) {
		return true;
	}

	return Boolean(job?.preview?.img_cover || job?.preview?.img_thumb || job?.preview?.img_2160);
};

const isFioAssetReady = (asset: any) => {
	return asset?.status === "ready.fio.asset.ready";
};

const isPreviewAssetReady = (asset: any) => {
	if (!asset) {
		return false;
	}

	if (asset.provider === "coconut") {
		return isCoconutJobReady(asset);
	}

	if (asset.provider === "image") {
		return isImageJobReady(asset);
	}

	return isFioAssetReady(asset);
};

const getCoconutFileName = (job: any, uploadId: string) => {
	return job?.fileName || job?.file_name || job?.sourceFileName || job?.source_file_name || job?.announcedFile?.fileName || uploadId;
};

const getCoconutFileType = (job: any) => {
	return job?.fileType || job?.file_type || job?.sourceFileType || job?.source_file_type || job?.asset_info?.file_type || "";
};

const getCoconutFileSize = (job: any) => {
	return job?.fileSize || job?.file_size || job?.sourceFileSize || job?.source_file_size || 0;
};

const getImageFileName = (job: any, uploadId: string) => {
	return job?.fileName || job?.file_name || job?.sourceFileName || job?.source_file_name || uploadId;
};

const getImageFileType = (job: any) => {
	return job?.fileType || job?.file_type || job?.sourceFileType || job?.source_file_type || job?.outputFormat?.contentType || "";
};

const getImageFileSize = (job: any) => {
	return job?.fileSize || job?.file_size || job?.sourceFileSize || job?.source_file_size || 0;
};

const getPreviewAssets = (versionValues: any) => {
	const fioAssets = Object.entries(versionValues?.fio_asset_ids || {}).map(([id, fioAsset]: any) => {
		const fioAssetId = fioAsset?.fio_asset_id || id;

		return {
			...fioAsset,
			provider: "fio",

			id: fioAssetId,
			selection_id: fioAssetId,
			fio_asset_id: fioAssetId,
			preview_asset_id: fioAssetId,

			file_name: fioAsset?.file_name || fioAsset?.name || fioAssetId,
			file_type: fioAsset?.file_type || "",
			file_size: fioAsset?.file_size || fioAsset?.asset_info?.file_size || 0,
			status: fioAsset?.status || "",
		};
	});

	const coconutAssets = Object.entries(versionValues?.coconut_jobs || {}).map(([uploadId, job]: any) => {
		return {
			...(job || {}),
			provider: "coconut",

			id: uploadId,
			selection_id: uploadId,
			preview_asset_id: uploadId,

			upload_id: uploadId,
			uploadId,
			coconut_upload_id: uploadId,

			fio_asset_id: uploadId,

			file_name: getCoconutFileName(job, uploadId),
			file_type: getCoconutFileType(job),
			file_size: getCoconutFileSize(job),
			status: job?.status || "",
			asset_info: job?.asset_info || null,
		};
	});

	const imageAssets = Object.entries(versionValues?.image_jobs || {}).map(([uploadId, job]: any) => {
		return {
			...(job || {}),
			provider: "image",

			id: uploadId,
			selection_id: uploadId,
			preview_asset_id: uploadId,

			upload_id: uploadId,
			uploadId,
			image_upload_id: uploadId,

			fio_asset_id: uploadId,

			file_name: getImageFileName(job, uploadId),
			file_type: getImageFileType(job),
			file_size: getImageFileSize(job),
			status: job?.status || "",
			asset_info: job?.asset_info || null,
		};
	});

	return [...fioAssets, ...coconutAssets, ...imageAssets].filter((item: any) => {
		return Boolean(item?.preview_asset_id || item?.fio_asset_id || item?.uploadId || item?.id);
	});
};

const getPreviewAssetId = (previewAsset: any) => {
	if (!previewAsset || typeof previewAsset !== "object") {
		return "";
	}

	return previewAsset?.preview_asset_id || previewAsset?.id || previewAsset?.fio_asset_id || previewAsset?.upload_id || previewAsset?.uploadId || previewAsset?.coconut_upload_id || previewAsset?.image_upload_id || "";
};

const getPreviewAssetSortName = (previewAsset: any) => {
	return String(previewAsset?.sourceFileName || previewAsset?.source_file_name || previewAsset?.file_name || previewAsset?.fileName || previewAsset?.sourcePath || previewAsset?.filePath || getPreviewAssetId(previewAsset) || "");
};

const getAnnouncedFileSortValue = (announcedFile: any, fallbackIndex: number) => {
	const explicitOrder = announcedFile?.sortIndex ?? announcedFile?.sort_index ?? announcedFile?.order ?? announcedFile?.index;

	const parsed = Number(explicitOrder);

	if (Number.isFinite(parsed)) {
		return parsed;
	}

	return fallbackIndex;
};

const getAnnouncedFileOrderMaps = (versionValues: any) => {
	const announcedEntries = Object.entries(versionValues?.announced_files || {});

	const byKey: Record<string, number> = {};
	const byFileName: Record<string, number> = {};
	const byFilePath: Record<string, number> = {};

	announcedEntries.forEach(([key, announcedFile]: any, index: number) => {
		const sortValue = getAnnouncedFileSortValue(announcedFile, index);

		const fileName = String(announcedFile?.fileName || announcedFile?.file_name || announcedFile?.name || "").toLowerCase();
		const filePath = String(announcedFile?.filePath || announcedFile?.file_path || announcedFile?.expectedPath || announcedFile?.expected_path || "").toLowerCase();

		byKey[String(key)] = sortValue;

		if (fileName) {
			byFileName[fileName] = sortValue;
		}

		if (filePath) {
			byFilePath[filePath] = sortValue;
		}
	});

	return {
		byKey,
		byFileName,
		byFilePath,
	};
};

const getPreviewAssetAnnouncedIndex = (previewAsset: any, announcedOrderMaps: any) => {
	const announcedFileKey = String(previewAsset?.announcedFileKey || previewAsset?.announced_file_key || "");
	const fileName = String(previewAsset?.fileName || previewAsset?.file_name || previewAsset?.sourceFileName || previewAsset?.source_file_name || "").toLowerCase();
	const filePath = String(previewAsset?.filePath || previewAsset?.sourcePath || previewAsset?.source_path || "").toLowerCase();

	if (announcedFileKey && Number.isFinite(announcedOrderMaps.byKey?.[announcedFileKey])) {
		return announcedOrderMaps.byKey[announcedFileKey];
	}

	if (filePath && Number.isFinite(announcedOrderMaps.byFilePath?.[filePath])) {
		return announcedOrderMaps.byFilePath[filePath];
	}

	if (fileName && Number.isFinite(announcedOrderMaps.byFileName?.[fileName])) {
		return announcedOrderMaps.byFileName[fileName];
	}

	return Number.MAX_SAFE_INTEGER;
};

const sortPreviewAssets = (previewAssets: any[], versionValues: any = {}) => {
	const announcedOrderMaps = getAnnouncedFileOrderMaps(versionValues);

	return previewAssets
		.slice()
		.map((previewAsset: any, originalIndex: number) => ({
			previewAsset,
			originalIndex,
			announcedIndex: getPreviewAssetAnnouncedIndex(previewAsset, announcedOrderMaps),
		}))
		.sort((a: any, b: any) => {
			if (a.announcedIndex !== b.announcedIndex) {
				return a.announcedIndex - b.announcedIndex;
			}

			const nameCompare = getPreviewAssetSortName(a.previewAsset).localeCompare(getPreviewAssetSortName(b.previewAsset), undefined, {
				numeric: true,
				sensitivity: "base",
			});

			if (nameCompare !== 0) {
				return nameCompare;
			}

			const providerCompare = String(a.previewAsset?.provider || "").localeCompare(String(b.previewAsset?.provider || ""));

			if (providerCompare !== 0) {
				return providerCompare;
			}

			const idCompare = String(getPreviewAssetId(a.previewAsset)).localeCompare(String(getPreviewAssetId(b.previewAsset)));

			if (idCompare !== 0) {
				return idCompare;
			}

			return a.originalIndex - b.originalIndex;
		})
		.map(({ previewAsset }: any) => previewAsset);
};

const isFiniteNumber = (value: any) => {
	return typeof value === "number" && Number.isFinite(value);
};

const getPreviewDisplaySlots = (previewAssets: any[], versionValues: any = {}) => {
	const announcedCount = Number(versionValues?.announced_filecount || 0);
	const announcedOrderMaps = getAnnouncedFileOrderMaps(versionValues);

	const assetsWithIndex = previewAssets.map((previewAsset: any, originalIndex: number) => {
		const announcedIndex = getPreviewAssetAnnouncedIndex(previewAsset, announcedOrderMaps);

		return {
			previewAsset,
			originalIndex,
			announcedIndex,
			hasAnnouncedIndex: isFiniteNumber(announcedIndex) && announcedIndex !== Number.MAX_SAFE_INTEGER,
		};
	});

	const maxAnnouncedIndex = assetsWithIndex.reduce((max: number, item: any) => {
		if (!item.hasAnnouncedIndex) {
			return max;
		}

		return Math.max(max, item.announcedIndex);
	}, -1);

	const slotCount = Math.max(announcedCount, maxAnnouncedIndex + 1, previewAssets.length);

	const slots = Array.from({ length: slotCount }, (_item, slotIndex) => {
		return {
			slotIndex,
			asset: undefined as any,
			state: "empty",
			reason: "reserved",
		};
	});

	const unslotted: any[] = [];

	assetsWithIndex.forEach((item: any) => {
		if (item.hasAnnouncedIndex && item.announcedIndex >= 0 && item.announcedIndex < slots.length && !slots[item.announcedIndex].asset) {
			slots[item.announcedIndex] = {
				slotIndex: item.announcedIndex,
				asset: item.previewAsset,
				state: isPreviewAssetReady(item.previewAsset) ? "ready" : "loading",
				reason: "announced_index",
			};

			return;
		}

		unslotted.push(item);
	});

	unslotted
		.sort((a: any, b: any) => {
			const nameCompare = getPreviewAssetSortName(a.previewAsset).localeCompare(getPreviewAssetSortName(b.previewAsset), undefined, {
				numeric: true,
				sensitivity: "base",
			});

			if (nameCompare !== 0) {
				return nameCompare;
			}

			const idCompare = String(getPreviewAssetId(a.previewAsset)).localeCompare(String(getPreviewAssetId(b.previewAsset)));

			if (idCompare !== 0) {
				return idCompare;
			}

			return a.originalIndex - b.originalIndex;
		})
		.forEach((item: any) => {
			const emptySlot = slots.find((slot: any) => !slot.asset);

			if (emptySlot) {
				emptySlot.asset = item.previewAsset;
				emptySlot.state = isPreviewAssetReady(item.previewAsset) ? "ready" : "loading";
				emptySlot.reason = "fallback_empty_slot";
				return;
			}

			slots.push({
				slotIndex: slots.length,
				asset: item.previewAsset,
				state: isPreviewAssetReady(item.previewAsset) ? "ready" : "loading",
				reason: "fallback_extra_slot",
			});
		});

	return slots;
};

const getLegacyFioAssets = (versionValues: any) => {
	const fioAssetIds = versionValues?.fio_asset_ids || {};

	return Object.values(fioAssetIds)
		.map((item: any) => {
			const fioAssetId = item?.fio_asset_id || item?.id || "";

			return {
				...(item || {}),
				provider: "fio",
				id: fioAssetId,
				fio_asset_id: fioAssetId,
				preview_asset_id: fioAssetId,
				file_name: item?.file_name || item?.name || fioAssetId,
				file_type: item?.file_type || "",
				status: item?.status || "",
			};
		})
		.sort((a: any, b: any) => a?.file_name?.localeCompare(b?.file_name) || 1);
};

const getPreviewDebugRow = (previewAsset: any, index: number, versionValues: any, method = "current") => {
	if (!previewAsset || typeof previewAsset !== "object" || !Object.keys(previewAsset).length) {
		return {
			method,
			index,
			provider: "",
			id: "",
			short_id: "",
			file_name: "",
			fileName: "",
			sourceFileName: "",
			source_file_name: "",
			sourcePath: "",
			filePath: "",
			status: "",
			ready: false,
			announced_index: "",
			sort_name: "",
		};
	}

	const announcedOrderMaps = getAnnouncedFileOrderMaps(versionValues);
	const id = getPreviewAssetId(previewAsset);

	return {
		method,
		index,
		provider: previewAsset?.provider || "",
		id,
		short_id: getShortDebugValue(id),
		file_name: previewAsset?.file_name || "",
		fileName: previewAsset?.fileName || "",
		sourceFileName: previewAsset?.sourceFileName || "",
		source_file_name: previewAsset?.source_file_name || "",
		sourcePath: previewAsset?.sourcePath || previewAsset?.source_path || "",
		filePath: previewAsset?.filePath || previewAsset?.file_path || "",
		status: previewAsset?.status || "",
		ready: isPreviewAssetReady(previewAsset),
		announced_index: getPreviewAssetAnnouncedIndex(previewAsset, announcedOrderMaps),
		sort_name: getPreviewAssetSortName(previewAsset),
	};
};

const getPreviewDebugRows = (previewAssets: any[], versionValues: any, method = "current") => {
	return previewAssets.map((previewAsset: any, index: number) => getPreviewDebugRow(previewAsset, index, versionValues, method));
};

const getPreviewOrderSignature = (rows: any[]) => {
	return rows
		.map((row: any) => {
			return `${row.index}:${row.provider}:${row.id}:${row.announced_index}`;
		})
		.join(" || ");
};

const getPreviewStatusSignature = (rows: any[]) => {
	return rows
		.map((row: any) => {
			return `${row.provider}:${row.id}:${row.status}:${row.ready}`;
		})
		.join(" || ");
};

const getSlotDebugRow = (slot: number, expectedAsset: any, renderedAsset: any, versionValues: any, method = "current", slotMeta: any = {}) => {
	const expectedRow = getPreviewDebugRow(expectedAsset, slot - 1, versionValues, method);
	const renderedRow = getPreviewDebugRow(renderedAsset, slot - 1, versionValues, method);

	const hasExpected = Boolean(expectedAsset && typeof expectedAsset === "object" && Object.keys(expectedAsset).length);
	const hasRendered = Boolean(renderedAsset && typeof renderedAsset === "object" && Object.keys(renderedAsset).length);

	return {
		method,
		slot: `#${slot}`,
		slotIndex: slotMeta?.slotIndex ?? slot - 1,
		slotReason: slotMeta?.reason || "",
		expected_provider: expectedRow.provider,
		expected_id: expectedRow.id,
		expected_short_id: expectedRow.short_id,
		expected_file_name: expectedRow.file_name,
		expected_sort_name: expectedRow.sort_name,
		expected_status: expectedRow.status,
		expected_ready: expectedRow.ready,
		expected_announced_index: expectedRow.announced_index,
		rendered_provider: renderedRow.provider,
		rendered_id: renderedRow.id,
		rendered_short_id: renderedRow.short_id,
		rendered_status: renderedRow.status,
		rendered: hasRendered,
		state: hasRendered ? "rendered" : hasExpected ? "loading/not-ready" : "empty",
	};
};

const getSlotSignature = (rows: any[]) => {
	return rows
		.map((row: any) => {
			return `${row.slot}:${row.expected_provider}:${row.expected_id}:${row.expected_announced_index}:${row.rendered_provider}:${row.rendered_id}:${row.rendered}`;
		})
		.join(" || ");
};

const getSlotStatusSignature = (rows: any[]) => {
	return rows
		.map((row: any) => {
			return `${row.slot}:${row.expected_id}:${row.expected_status}:${row.expected_ready}:${row.rendered_id}:${row.rendered_status}:${row.rendered}`;
		})
		.join(" || ");
};

const getLegacyFioDebugState = (versionValues: any) => {
	const versionFileCount = versionValues?.announced_filecount;
	const fioAssetsArr = getLegacyFioAssets(versionValues);

	const fioCurFav =
		fioAssetsArr?.find((item: any) => {
			return item.fio_asset_id === versionValues?.cur_preview_fav;
		}) || {};

	const favValid = Object.keys(fioCurFav || {}).length > 0;

	let fioAssetSpliced = fioAssetsArr.slice();

	if (favValid) {
		const favIndex = fioAssetSpliced.indexOf(fioCurFav);

		if (favIndex >= 0) {
			fioAssetSpliced.splice(favIndex, 1);
		}
	}

	let image00: any = "";
	let image01: any = "";
	let image02: any = "";

	const firstImage: any = fioCurFav;
	const secondImage: any = fioAssetSpliced[0];
	const thirdImage: any = fioAssetSpliced[1];

	if (typeof firstImage !== "undefined" && firstImage?.status === "ready.fio.asset.ready") {
		image00 = firstImage;
	}

	if (typeof secondImage !== "undefined" && secondImage?.status === "ready.fio.asset.ready") {
		image01 = secondImage;
	}

	if (typeof thirdImage !== "undefined" && thirdImage?.status === "ready.fio.asset.ready") {
		image02 = thirdImage;
	}

	const orderRows = getPreviewDebugRows(fioAssetsArr, versionValues, "legacy_fio");
	const slotRows = [getSlotDebugRow(1, firstImage, image00, versionValues, "legacy_fio"), getSlotDebugRow(2, secondImage, image01, versionValues, "legacy_fio"), getSlotDebugRow(3, thirdImage, image02, versionValues, "legacy_fio")];

	return {
		method: "legacy_fio",
		versionFileCount,
		curPreviewFav: versionValues?.cur_preview_fav || "",
		favValid,
		fioAssetCount: fioAssetsArr.length,
		orderSignature: getPreviewOrderSignature(orderRows),
		orderStatusSignature: getPreviewStatusSignature(orderRows),
		slotSignature: getSlotSignature(slotRows),
		slotStatusSignature: getSlotStatusSignature(slotRows),
		orderRows,
		slotRows,
	};
};

export default function VersionCard({ index, asset, versionId, sx, jobIdent, isJobNameShown, markDeleted }: any) {
	const authController = useAuthController();
	const _versionId = versionId;

	const [loaded, setLoaded] = useState<boolean>(false);
	const loadedDebounced = useDebounce<boolean>(loaded, 250);
	const [version, setVersion] = useState<any>({});
	const versionListener = useRef<any>();
	const previewOrderDebugRef = useRef<any>(null);

	const dataSource = useDataSource();

	const onVersionsUpdate = (evt: any) => {
		setVersion((prevState: any) => ({ ...prevState, ...evt }));
	};

	useEffect(() => {
		if (typeof versionListener.current === "undefined" && dataSource.listenEntity && _versionId) {
			versionListener.current = dataSource.listenEntity({
				path: versionsCollection.path,
				entityId: _versionId,
				collection: versionsCollection,
				onUpdate: (evt) => onVersionsUpdate(evt),
			});

			return () => {
				versionListener.current();
				versionListener.current = undefined;
			};
		}
	}, [dataSource, _versionId]);

	const [isHovering, setIsHovering] = useState(false);

	const handleMouseOver = () => {
		setIsHovering(true);
	};

	const handleMouseOut = () => {
		setIsHovering(false);
	};

	const versionValues = version?.values || {};

	const version_number = versionValues?.asset_version_number;
	const announced_file_count = Number(versionValues?.announced_filecount || 0);
	const version_string = (version_number < 10 ? "00" : version_number < 100 ? "0" : "") + version_number?.toString();

	const preview_assets_all = sortPreviewAssets(getPreviewAssets(versionValues), versionValues);
	const preview_display_slots = getPreviewDisplaySlots(preview_assets_all, versionValues);
	const version_file_count = preview_display_slots.length;

	const preview_assets_all_ready =
		preview_assets_all.length > 0 &&
		preview_assets_all.every((item: any) => {
			return isPreviewAssetReady(item);
		});

	let filetype_string = "";

	if (preview_assets_all_ready) {
		const filetypes = preview_assets_all.map((item: any) => getFileTypeLabel(item?.file_type)).filter(Boolean);
		filetype_string = Array.from(new Set(filetypes)).join(", ") || " ";
	}

	let image_00: any = "";
	let image_01: any = "";
	let image_02: any = "";

	const firstSlot = preview_display_slots[0];
	const secondSlot = preview_display_slots[1];
	const thirdSlot = preview_display_slots[2];

	const firstImage: any = firstSlot?.asset;
	const secondImage: any = secondSlot?.asset;
	const thirdImage: any = thirdSlot?.asset;

	if (typeof firstImage !== "undefined" && isPreviewAssetReady(firstImage)) {
		image_00 = firstImage;
	}

	if (typeof secondImage !== "undefined" && isPreviewAssetReady(secondImage)) {
		image_01 = secondImage;
	}

	if (typeof thirdImage !== "undefined" && isPreviewAssetReady(thirdImage)) {
		image_02 = thirdImage;
	}

	const currentOrderRows = getPreviewDebugRows(preview_assets_all, versionValues, "current_mixed_sorted_assets");
	const currentSlotRows = [
		getSlotDebugRow(1, firstImage, image_00, versionValues, "current_display_slots", firstSlot),
		getSlotDebugRow(2, secondImage, image_01, versionValues, "current_display_slots", secondSlot),
		getSlotDebugRow(3, thirdImage, image_02, versionValues, "current_display_slots", thirdSlot),
	];

	const currentOrderSignature = getPreviewOrderSignature(currentOrderRows);
	const currentOrderStatusSignature = getPreviewStatusSignature(currentOrderRows);
	const currentSlotSignature = getSlotSignature(currentSlotRows);
	const currentSlotStatusSignature = getSlotStatusSignature(currentSlotRows);

	const legacyFioDebugState = getLegacyFioDebugState(versionValues);

	const combinedDebugSignature = [
		currentOrderSignature,
		currentOrderStatusSignature,
		currentSlotSignature,
		currentSlotStatusSignature,
		legacyFioDebugState.orderSignature,
		legacyFioDebugState.orderStatusSignature,
		legacyFioDebugState.slotSignature,
		legacyFioDebugState.slotStatusSignature,
		versionValues?.status || "",
		versionValues?.cur_preview_fav || "",
		announced_file_count,
		version_file_count,
	].join(" ### ");

	useEffect(() => {
		if (!isPreviewOrderDebugEnabled()) {
			return;
		}

		const nextDebugState = {
			versionId: version?.id || _versionId,
			versionNumber: version_number,
			versionStatus: versionValues?.status || "",
			assetStatus: asset?.values?.status || "",
			curPreviewFav: versionValues?.cur_preview_fav || "",
			announcedFileCount: announced_file_count,
			currentVersionFileCount: version_file_count,

			counts: {
				fio_asset_ids: Object.keys(versionValues?.fio_asset_ids || {}).length,
				coconut_jobs: Object.keys(versionValues?.coconut_jobs || {}).length,
				image_jobs: Object.keys(versionValues?.image_jobs || {}).length,
				current_preview_assets_all: preview_assets_all.length,
				display_slots: preview_display_slots.length,
			},

			currentMixed: {
				method: "current_mixed_sorted_assets",
				orderSignature: currentOrderSignature,
				orderStatusSignature: currentOrderStatusSignature,
				orderRows: currentOrderRows,
			},

			currentDisplaySlots: {
				method: "current_display_slots",
				firstExpectedId: getPreviewAssetId(firstImage),
				secondExpectedId: getPreviewAssetId(secondImage),
				thirdExpectedId: getPreviewAssetId(thirdImage),
				firstRenderedId: getPreviewAssetId(image_00),
				secondRenderedId: getPreviewAssetId(image_01),
				thirdRenderedId: getPreviewAssetId(image_02),
				slotSignature: currentSlotSignature,
				slotStatusSignature: currentSlotStatusSignature,
				slotRows: currentSlotRows,
				rawSlots: preview_display_slots.map((slot: any) => {
					return {
						slotIndex: slot.slotIndex,
						reason: slot.reason,
						state: slot.state,
						provider: slot.asset?.provider || "",
						id: getPreviewAssetId(slot.asset),
						file_name: slot.asset?.file_name || slot.asset?.fileName || "",
						status: slot.asset?.status || "",
						ready: isPreviewAssetReady(slot.asset),
					};
				}),
			},

			legacyFio: legacyFioDebugState,
		};

		const previousDebugState = previewOrderDebugRef.current;

		if (!previousDebugState) {
			console.groupCollapsed(`[PreviewOrder:init] version=${nextDebugState.versionId} v${nextDebugState.versionNumber || ""}`);
			console.log("state", nextDebugState);
			console.table(nextDebugState.currentMixed.orderRows);
			console.table(nextDebugState.currentDisplaySlots.slotRows);
			console.table(nextDebugState.legacyFio.orderRows);
			console.table(nextDebugState.legacyFio.slotRows);
			console.groupEnd();

			console.log("[PreviewOrder:init:json]", stringifyPreviewDebugState(nextDebugState));

			previewOrderDebugRef.current = nextDebugState;
			return;
		}

		const currentOrderChanged = previousDebugState.currentMixed?.orderSignature !== nextDebugState.currentMixed.orderSignature;
		const currentOrderStatusChanged = previousDebugState.currentMixed?.orderStatusSignature !== nextDebugState.currentMixed.orderStatusSignature;
		const currentSlotChanged = previousDebugState.currentDisplaySlots?.slotSignature !== nextDebugState.currentDisplaySlots.slotSignature;
		const currentSlotStatusChanged = previousDebugState.currentDisplaySlots?.slotStatusSignature !== nextDebugState.currentDisplaySlots.slotStatusSignature;

		const legacyOrderChanged = previousDebugState.legacyFio?.orderSignature !== nextDebugState.legacyFio.orderSignature;
		const legacyOrderStatusChanged = previousDebugState.legacyFio?.orderStatusSignature !== nextDebugState.legacyFio.orderStatusSignature;
		const legacySlotChanged = previousDebugState.legacyFio?.slotSignature !== nextDebugState.legacyFio.slotSignature;
		const legacySlotStatusChanged = previousDebugState.legacyFio?.slotStatusSignature !== nextDebugState.legacyFio.slotStatusSignature;

		if (!currentOrderChanged && !currentOrderStatusChanged && !currentSlotChanged && !currentSlotStatusChanged && !legacyOrderChanged && !legacyOrderStatusChanged && !legacySlotChanged && !legacySlotStatusChanged) {
			return;
		}

		console.groupCollapsed(
			`[PreviewOrder:change] version=${nextDebugState.versionId} v${nextDebugState.versionNumber || ""} currentOrder=${currentOrderChanged} currentSlot=${currentSlotChanged} currentStatus=${
				currentOrderStatusChanged || currentSlotStatusChanged
			} legacyOrder=${legacyOrderChanged} legacySlot=${legacySlotChanged}`
		);

		console.log("previous state", previousDebugState);
		console.log("next state", nextDebugState);

		if (currentOrderChanged) {
			console.log("previous current mixed sorted order");
			console.table(previousDebugState.currentMixed?.orderRows || []);

			console.log("next current mixed sorted order");
			console.table(nextDebugState.currentMixed.orderRows);
		}

		if (currentSlotChanged) {
			console.log("previous current display slots");
			console.table(previousDebugState.currentDisplaySlots?.slotRows || []);

			console.log("next current display slots");
			console.table(nextDebugState.currentDisplaySlots.slotRows);
		}

		if (currentOrderStatusChanged || currentSlotStatusChanged) {
			console.log("previous current slot/status");
			console.table(previousDebugState.currentDisplaySlots?.slotRows || []);

			console.log("next current slot/status");
			console.table(nextDebugState.currentDisplaySlots.slotRows);
		}

		if (legacyOrderChanged) {
			console.log("previous legacy FIO sorted order");
			console.table(previousDebugState.legacyFio?.orderRows || []);

			console.log("next legacy FIO sorted order");
			console.table(nextDebugState.legacyFio.orderRows);
		}

		if (legacySlotChanged) {
			console.log("previous legacy FIO preview slots");
			console.table(previousDebugState.legacyFio?.slotRows || []);

			console.log("next legacy FIO preview slots");
			console.table(nextDebugState.legacyFio.slotRows);
		}

		console.groupEnd();

		console.log(
			"[PreviewOrder:change:json]",
			stringifyPreviewDebugState({
				diff: {
					currentOrderChanged,
					currentOrderStatusChanged,
					currentSlotChanged,
					currentSlotStatusChanged,
					legacyOrderChanged,
					legacyOrderStatusChanged,
					legacySlotChanged,
					legacySlotStatusChanged,
				},
				previous: previousDebugState,
				next: nextDebugState,
			})
		);

		previewOrderDebugRef.current = nextDebugState;
	}, [combinedDebugSignature, version?.id, _versionId, version_number, asset?.values?.status]);

	const renderPreviewDebugBadge = (slotNumber: number, expectedAsset: any, renderedAsset: any, sxOverride: any = {}) => {
		if (!isPreviewOrderDebugEnabled()) {
			return null;
		}

		const hasExpected = Boolean(expectedAsset && typeof expectedAsset === "object" && Object.keys(expectedAsset).length);
		const hasRendered = Boolean(renderedAsset && typeof renderedAsset === "object" && Object.keys(renderedAsset).length);

		const debugAsset = hasExpected ? expectedAsset : hasRendered ? renderedAsset : {};
		const provider = debugAsset?.provider || "empty";
		const id = getPreviewAssetId(debugAsset) || "";
		const shortId = getShortDebugValue(id || "none");
		const status = debugAsset?.status || "";
		const state = hasRendered ? "ready" : hasExpected ? "loading" : "empty";

		const label = `#${slotNumber} ${provider}:${shortId} ${state}`;
		const title = [
			`slot: #${slotNumber}`,
			`state: ${state}`,
			`provider: ${provider}`,
			`id: ${id || "none"}`,
			`file: ${debugAsset?.file_name || debugAsset?.fileName || debugAsset?.sourceFileName || debugAsset?.source_file_name || ""}`,
			`status: ${status}`,
			`ready: ${hasExpected ? isPreviewAssetReady(expectedAsset) : false}`,
		].join("\n");

		return (
			<Box title={title} sx={{ ...styles.preview_debug_badge, ...sxOverride }}>
				{label}
			</Box>
		);
	};

	const commit_messsage = versionValues?.commit_message;

	const openPlayerForPreviewAsset = (previewAsset: any) => {
		const previewAssetId = getPreviewAssetId(previewAsset);

		if (!previewAssetId) {
			return;
		}

		authController?.extra?.playerDialogHandleOpen(versionValues?.asset?.id, version?.id, previewAssetId);
	};

	const renderPreviewImages = () => {
		const firstItem = image_00;
		const secondItem = image_01;
		const thirdItem = image_02;

		// Parent keys are based on the expected slot asset, not only the rendered item.
		// This keeps slot identity stable but remounts if the slot is reassigned.
		const firstKey = `slot-1-${getPreviewAssetId(firstImage) || "empty"}`;
		const secondKey = `slot-2-${getPreviewAssetId(secondImage) || "empty"}`;
		const thirdKey = `slot-3-${getPreviewAssetId(thirdImage) || "empty"}`;

		if (version_file_count > 0 && version_file_count < 3) {
			switch (version_file_count) {
				case 1:
					return (
						<Box sx={styles.container_rows}>
							<VersionCardPreviewItem key={firstKey} asset={firstItem} status={firstImage?.status} type={"full"} version={version} filecount={version_file_count} strict_asset_id={true} />

							{renderPreviewDebugBadge(1, firstImage, firstItem)}
						</Box>
					);

				case 2:
					return (
						<Box sx={styles.container_rows}>
							<VersionCardPreviewItem key={firstKey} asset={firstItem} status={firstImage?.status} type={"half_left"} version={version} filecount={version_file_count} strict_asset_id={true} />

							<VersionCardPreviewItem key={secondKey} asset={secondItem} status={secondImage?.status} type={"half_right"} version={version} filecount={version_file_count} strict_asset_id={true} />

							{renderPreviewDebugBadge(1, firstImage, firstItem, { left: 6 })}
							{renderPreviewDebugBadge(2, secondImage, secondItem, { left: "calc(50% + 6px)" })}
						</Box>
					);

				default:
					break;
			}
		} else if (version_file_count > 2) {
			return (
				<Box sx={styles.container_rows}>
					<VersionCardPreviewItem key={firstKey} asset={firstItem} status={firstImage?.status} type={"half_left"} version={version} filecount={version_file_count} strict_asset_id={true} />

					{renderPreviewDebugBadge(1, firstImage, firstItem, { left: 6 })}

					<Box sx={styles.container_row_item_right}>
						<VersionCardPreviewItem key={secondKey} asset={secondItem} status={secondImage?.status} type={"third_right_top"} version={version} filecount={version_file_count} strict_asset_id={true} />

						{renderPreviewDebugBadge(2, secondImage, secondItem, { top: 14, left: 9 })}

						<VersionCardPreviewItem
							key={thirdKey}
							asset={thirdItem}
							onclick={() => openPlayerForPreviewAsset(thirdImage)}
							status={thirdImage?.status}
							type={"third_right_bottom"}
							version={version}
							filecount={version_file_count}
							strict_asset_id={true}
						/>

						{renderPreviewDebugBadge(3, thirdImage, thirdItem, { top: "calc(50% + 14px)", left: 9 })}
					</Box>
				</Box>
			);
		}

		return <Box sx={{ position: "relative", height: { sm: "250px", xs: "125px" }, width: "100%", m: "8px" }}>{renderPreviewDebugBadge(1, firstImage, image_00)}</Box>;
	};

	const status = asset?.values?.status;
	const version_status = versionValues?.status;
	const minutes = String(versionValues?.created_on?.getMinutes()).padStart(2, "0");
	const hours = String(versionValues?.created_on?.getHours()).padStart(2, "0");

	useEffect(() => {
		const nextLoaded = typeof versionValues?.created_on !== "undefined";
		setLoaded(nextLoaded);
	}, [versionValues?.created_on]);

	return (
		<Box
			key={index}
			sx={{
				boxSizing: "border-box",
				"& *, & *::before, & *::after": {
					boxSizing: "border-box",
				},
				...sx,
				maxWidth: "100%",
				minWidth: { sm: 500, xs: "100%" },
				width: "100%",
				p: "8px",
				borderRadius: 2,
				display: "flex",
				justifyContent: "center",
			}}
		>
			<Box sx={{ display: { xs: "none", sm: "inherit" }, width: 50, mr: 1, opacity: loadedDebounced ? 1 : 0, transition: ".5s cubic-bezier(.39,.41,.33,.99)" }}>
				{hours}:{minutes}
			</Box>

			<Card sx={{ width: "100%", minWidth: { sm: 400, xs: "100%" }, maxWidth: { sm: 400, xs: "100%" }, backgroundColor: "background.paper", backgroundImage: "none" }} onMouseOver={handleMouseOver} onMouseOut={handleMouseOut}>
				{version ? (
					<Box sx={{ opacity: loadedDebounced ? 1 : 0, transition: ".5s cubic-bezier(.39,.41,.33,.99)" }}>
						<Box sx={{ display: { sm: "none", xs: markDeleted ? "none" : "inherit" }, width: "100%", pl: 1, pr: 1, mt: 1, mr: 1, opacity: loadedDebounced ? 1 : 0, transition: ".5s cubic-bezier(.39,.41,.33,.99)" }}>
							<b>{versionValues?.author_name}</b>
							{`  at ${hours}:${minutes}`}
						</Box>

						{markDeleted ? null : renderPreviewImages()}

						<Box sx={{ display: "flex" }}>
							<CardContent sx={{ width: "100%" }}>
								<Box sx={{ display: "flex", flexGrow: 1, flexDirection: "row", alignItems: "center" }}>
									<Box sx={{ flexGrow: 1 }}>
										{markDeleted ? null : (
											<Box
												sx={{
													display: { sm: markDeleted ? "none" : "flex", xs: "none" },
													alignItems: "center",
													height: "22px",
												}}
											>
												{versionValues?.origin === "unreal" ? (
													<Box sx={{ display: "flex", mr: 0.5 }}>
														<Tooltip title={"Unreal Engine Boost Plugin"}>
															<Box component={UnrealEngineLogoIcon} color="inherit" sx={{ mr: 0.5, width: 20, color: "white" }} />
														</Tooltip>
														&#183;
													</Box>
												) : null}

												{status && !status?.startsWith("ready") ? (
													<Tooltip title={status ? status : ""}>
														<Box component={ErrorOutlineIcon} color="inherit" sx={{ mr: 0.5, width: 20, color: "orange" }} />
													</Tooltip>
												) : null}

												{filetype_string !== "" ? null : (
													<Tooltip title={version_status ? version_status : status || ""}>
														<Box component={UpdateIcon} color="inherit" sx={{ mr: 0.5, width: 20, color: "white" }} />
													</Tooltip>
												)}

												<Typography variant="body2" color="text.secondary" align={"left"}>
													{version_file_count} {version_file_count > 1 ? "items" : "item"} &#183; {filetype_string !== "" ? filetype_string : version_status}
												</Typography>
											</Box>
										)}

										<Box sx={{ display: "flex", mt: { sm: markDeleted ? -1 : 0.5, xs: markDeleted ? -1 : -2 } }}>
											<Typography variant="body1" color="text.primary" align={"left"} sx={{ opacity: markDeleted ? 0.5 : 1, bgcolor: "rgba(0,0,0,0.5)", ml: -1, pl: 1, pr: 1, borderRadius: "4px" }}>
												<b>{isJobNameShown ? asset?.values?.name : asset?.values?.name?.replace(jobIdent + "_", "")}</b>
											</Typography>

											<Typography variant="body1" color="text.primary" align={"left"} sx={{ opacity: markDeleted ? 0.5 : 1, bgcolor: "rgba(255,255,255,0.08)", ml: 0.5, pl: 1, pr: 1, borderRadius: "4px" }}>
												<b>v{version_number}</b>
											</Typography>

											{markDeleted ? (
												<Typography
													variant="body2"
													color="text.primary"
													align={"left"}
													sx={{ display: "flex", justifyContent: "center", alignItems: "center", opacity: markDeleted ? 0.5 : 1, bgcolor: "error.main", ml: 0.5, pl: 1, pr: 1, borderRadius: "4px" }}
												>
													<LayersClearIcon />
												</Typography>
											) : null}

											<Box sx={{ flexGrow: 1 }}></Box>
										</Box>

										<Box>
											<Typography sx={{ mt: 1, overflow: "hidden", textOverflow: "ellipsis", display: "-webkit-box", WebkitLineClamp: "1", WebkitBoxOrient: "vertical" }} variant="body2" color="text.secondary" align={"left"}>
												<b>
													{versionValues?.author_name}
													{versionValues?.author_name && commit_messsage ? ": " : ""}
												</b>

												{commit_messsage}
											</Typography>
										</Box>
									</Box>

									<Box>
										{!isHovering ? null : (
											<VersionMenu
												sx={{ display: { sm: markDeleted ? "none" : "flex", xs: "none" }, ml: "auto", pointerEvents: "auto" }}
												fio_asset_ids={versionValues?.fio_asset_ids}
												coconut_jobs={versionValues?.coconut_jobs}
												image_jobs={versionValues?.image_jobs}
												preview_assets={preview_assets_all}
												single_link={versionValues?.fio_review_link?.short_url}
												single_password={versionValues?.fio_review_link?.password}
												version_number_string={version_string}
												all_link={asset?.values?.fio_review_link?.short_url}
												version_name={versionValues?.name}
												version_id={version?.id}
											/>
										)}
									</Box>
								</Box>
							</CardContent>
						</Box>
					</Box>
				) : null}
			</Card>

			<Box sx={{ display: { xs: "none", sm: "inherit" }, width: 50, ml: 1 }}></Box>
		</Box>
	);
}
